import type { NodePropertySchema } from '../../../types/node-properties';

/**
 * Data Table Node Schema
 * Built-in storage for structured data within EWA
 *
 * Operations:
 * - getAll: Get all rows with optional filtering
 * - getById: Get a single row by ID
 * - query: Execute a SQL query
 * - count: Count rows with optional filter
 * - insert: Insert a single row
 * - update: Update a single row by ID
 * - upsert: Insert or update based on key field
 * - delete: Delete a single row by ID
 * - append: Insert multiple rows
 * - replaceAll: Delete all rows and insert new ones
 * - bulkUpdate: Update rows matching a condition
 * - bulkDelete: Delete rows matching a condition
 */
const schema: NodePropertySchema = {
  nodeType: 'data-table',
  version: '1.0.0',

  properties: [
    // ==================================================================
    // OPERATION (first so user picks action before seeing other fields)
    // ==================================================================
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'getAll',
      options: [
        // Table management
        {
          name: 'Create Table',
          value: 'createTable',
          description: 'Create a new data table with a defined schema',
        },
        // Read operations
        {
          name: 'Get All Rows',
          value: 'getAll',
          description: 'Get all rows from the table with optional filtering',
        },
        {
          name: 'Get Row by ID',
          value: 'getById',
          description: 'Get a single row by its unique ID',
        },
        {
          name: 'Query (SQL)',
          value: 'query',
          description: 'Execute a SQL query on the table',
        },
        {
          name: 'Count Rows',
          value: 'count',
          description: 'Count the number of rows in the table',
        },
        // Single row write
        {
          name: 'Insert Row',
          value: 'insert',
          description: 'Insert a new row into the table',
        },
        {
          name: 'Update Row',
          value: 'update',
          description: 'Update an existing row by ID',
        },
        {
          name: 'Upsert Row',
          value: 'upsert',
          description: 'Insert a new row or update existing based on key field',
        },
        {
          name: 'Delete Row',
          value: 'delete',
          description: 'Delete a row by its ID',
        },
        // Bulk write
        {
          name: 'Append Rows',
          value: 'append',
          description: 'Insert multiple rows into the table',
        },
        {
          name: 'Replace All Rows',
          value: 'replaceAll',
          description: 'Delete all rows and insert new ones',
        },
        {
          name: 'Bulk Update',
          value: 'bulkUpdate',
          description: 'Update multiple rows matching a condition',
        },
        {
          name: 'Bulk Delete',
          value: 'bulkDelete',
          description: 'Delete multiple rows matching a condition',
        },
      ],
      description: 'The operation to perform on the data table',
    },

    // ==================================================================
    // TABLE SELECTOR (hidden for createTable and when createIfNotExists is true)
    // ==================================================================
    {
      key: 'tableId',
      label: 'Table',
      type: 'options',
      required: true,
      description: 'Select the data table to operate on',
      options: [],
      optionsSource: 'dataTables',
      displayOptions: {
        hide: {
          operation: ['createTable'],
          createIfNotExists: [true],
        },
      },
    },

    // ==================================================================
    // CREATE TABLE OPERATION
    // ==================================================================
    {
      key: 'newTableName',
      label: 'Table Name',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          operation: ['createTable'],
        },
      },
      default: '',
      placeholder: 'my_table or {{ $node["Config"].output.tableName }}',
      description: 'Name for the new table. Supports variable interpolation.',
      validation: [
        {
          type: 'required',
          message: 'Table name is required',
        },
      ],
    },
    {
      key: 'newTableDescription',
      label: 'Description',
      type: 'string',
      required: false,
      displayOptions: {
        show: {
          operation: ['createTable'],
        },
      },
      default: '',
      placeholder: 'Optional description for the table',
      description: 'A description of what this table is used for',
    },
    {
      key: 'newTableSchema',
      label: 'Schema',
      type: 'json',
      required: true,
      displayOptions: {
        show: {
          operation: ['createTable'],
        },
      },
      default: `{
  "columns": [
    { "name": "customerId", "type": "string", "required": true },
    { "name": "amount", "type": "number", "required": true },
    { "name": "status", "type": "string" },
    { "name": "createdDate", "type": "datetime" }
  ],
  "primaryKey": "customerId"
}`,
      typeOptions: {
        rows: 12,
      },
      description:
        'Table schema definition. Column types: string, number, boolean, datetime, date, array, object, json.',
      validation: [
        {
          type: 'required',
          message: 'Schema is required',
        },
      ],
    },
    {
      key: 'failIfExists',
      label: 'Fail if table already exists',
      type: 'boolean',
      default: false,
      displayOptions: {
        show: {
          operation: ['createTable'],
        },
      },
      description: 'If unchecked, returns existing table info instead of failing.',
    },

    // ==================================================================
    // CREATE IF NOT EXISTS (for write operations)
    // ==================================================================
    {
      key: 'createIfNotExists',
      label: "Create table if it doesn't exist",
      type: 'boolean',
      default: false,
      displayOptions: {
        show: {
          operation: ['insert', 'update', 'upsert', 'append', 'replaceAll', 'bulkUpdate'],
        },
      },
      description:
        "Automatically create the table with schema inferred from the data if it doesn't exist.",
    },
    {
      key: 'newTableNameForCreate',
      label: 'New Table Name',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          createIfNotExists: [true],
          operation: ['insert', 'update', 'upsert', 'append', 'replaceAll', 'bulkUpdate'],
        },
      },
      default: '',
      placeholder: 'my_table',
      description: 'Name for the table if it needs to be created.',
      validation: [
        {
          type: 'required',
          message: 'Table name is required when "Create if not exists" is enabled',
        },
      ],
    },

    // ==================================================================
    // GET ALL - OPTIONS
    // ==================================================================
    {
      key: 'filter',
      label: 'Filter',
      type: 'string',
      displayOptions: {
        show: {
          operation: ['getAll', 'count'],
        },
      },
      default: '',
      placeholder: 'status = "active"',
      description: 'Optional filter condition (SQL WHERE syntax)',
    },
    {
      key: 'orderBy',
      label: 'Order By',
      type: 'string',
      displayOptions: {
        show: {
          operation: ['getAll'],
        },
      },
      default: '',
      placeholder: 'created_at DESC',
      description: 'Sort order for results',
    },
    {
      key: 'limit',
      label: 'Limit',
      type: 'number',
      displayOptions: {
        show: {
          operation: ['getAll'],
        },
      },
      default: 100,
      placeholder: '100',
      description: 'Maximum number of rows to return',
      typeOptions: {
        minValue: 1,
        maxValue: 10000,
      },
    },

    // ==================================================================
    // GET BY ID / UPDATE / DELETE - ROW ID
    // ==================================================================
    {
      key: 'rowId',
      label: 'Row ID',
      type: 'string',
      displayOptions: {
        show: {
          operation: ['getById'],
        },
      },
      placeholder: '{{ $node["NodeName"].string }}',
      required: true,
      description: 'The unique ID of the row to retrieve',
      validation: [
        {
          type: 'required',
          message: 'Row ID is required',
        },
      ],
    },
    {
      key: 'rowIdForUpdate',
      label: 'Row ID',
      type: 'string',
      displayOptions: {
        show: {
          operation: ['update', 'delete'],
        },
      },
      placeholder: '{{ $node["NodeName"].string }}',
      required: true,
      description: 'The unique ID of the row to update or delete',
      validation: [
        {
          type: 'required',
          message: 'Row ID is required',
        },
      ],
    },

    // ==================================================================
    // QUERY - SQL
    // ==================================================================
    {
      key: 'query',
      label: 'SQL Query',
      type: 'string',
      typeOptions: {
        rows: 8,
        editor: 'code',
        editorLanguage: 'sql',
      },
      displayOptions: {
        show: {
          operation: ['query'],
        },
      },
      default: 'SELECT * FROM @table WHERE status = \'active\'',
      placeholder: 'SELECT * FROM @table',
      required: true,
      description:
        'SQL query to execute. Use @table as the table reference. Column names from your schema are automatically translated.',
      validation: [
        {
          type: 'required',
          message: 'SQL query is required',
        },
      ],
    },

    // ==================================================================
    // INSERT / UPDATE / UPSERT - DATA
    // ==================================================================
    {
      key: 'data',
      label: 'Row Data',
      type: 'json',
      displayOptions: {
        show: {
          operation: ['insert', 'update', 'upsert'],
        },
      },
      default: '{\n  "name": "{{ $json.name }}",\n  "email": "{{ $json.email }}"\n}',
      typeOptions: {
        rows: 10,
      },
      required: true,
      description: 'The data to insert or update as a JSON object',
      validation: [
        {
          type: 'required',
          message: 'Row data is required',
        },
      ],
    },

    // ==================================================================
    // UPSERT - KEY FIELD
    // ==================================================================
    {
      key: 'upsertKey',
      label: 'Key Field',
      type: 'string',
      displayOptions: {
        show: {
          operation: ['upsert'],
        },
      },
      default: 'id',
      placeholder: 'email',
      required: true,
      description:
        'The field to use as the unique key for determining insert vs update',
      validation: [
        {
          type: 'required',
          message: 'Key field is required for upsert',
        },
      ],
    },

    // ==================================================================
    // APPEND / REPLACE ALL - ROWS DATA
    // ==================================================================
    {
      key: 'rows',
      label: 'Rows Data',
      type: 'string',
      displayOptions: {
        show: {
          operation: ['append', 'replaceAll'],
        },
      },
      placeholder: '{{ $node["NodeName"].array }}',
      required: true,
      description: 'Array of row objects to insert. Use expression to pass input data.',
      validation: [
        {
          type: 'required',
          message: 'Rows data is required',
        },
      ],
    },

    // ==================================================================
    // REPLACE ALL - CONFIRMATION
    // ==================================================================
    {
      key: 'confirmReplaceAll',
      label: 'Confirm Replace All',
      type: 'boolean',
      displayOptions: {
        show: {
          operation: ['replaceAll'],
        },
      },
      default: false,
      description:
        'Check this box to confirm you want to delete all existing rows before inserting new ones. This action cannot be undone.',
    },

    // ==================================================================
    // BULK UPDATE / DELETE - CONDITION
    // ==================================================================
    {
      key: 'bulkCondition',
      label: 'Condition',
      type: 'string',
      displayOptions: {
        show: {
          operation: ['bulkUpdate', 'bulkDelete'],
        },
      },
      default: '',
      placeholder: 'status = "inactive" AND updated_at < "2024-01-01"',
      required: true,
      description: 'SQL WHERE condition to match rows for the operation',
      validation: [
        {
          type: 'required',
          message: 'Condition is required for bulk operations',
        },
      ],
    },

    // ==================================================================
    // BULK UPDATE - UPDATE DATA
    // ==================================================================
    {
      key: 'bulkUpdateData',
      label: 'Update Data',
      type: 'json',
      displayOptions: {
        show: {
          operation: ['bulkUpdate'],
        },
      },
      default: '{\n  "status": "archived"\n}',
      typeOptions: {
        rows: 6,
      },
      required: true,
      description: 'The fields to update on matching rows',
      validation: [
        {
          type: 'required',
          message: 'Update data is required',
        },
      ],
    },
  ],
};

export default schema;
